import { useCallback, useEffect, useRef, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { i18_TRANSLATION } from "../../i18nReferences";
import { useTranslation } from "react-i18next";

const DeleteConfirmation = ({ onDelete, disabled, color, sx, icon, tooltip, label, ariaLabel }) => {
    const { t } = useTranslation([i18_TRANSLATION]);
    const [isConfirming, setIsConfirming] = useState(false);
    const deletionRequestTimerRef = useRef(undefined);

    const clearDeletionRequestTimer = useCallback(() => {
        if (deletionRequestTimerRef.current !== undefined) {
            clearTimeout(deletionRequestTimerRef.current);
            deletionRequestTimerRef.current = undefined;
        }
    }, []);

    useEffect(() => {
        return () => clearDeletionRequestTimer();
    }, [clearDeletionRequestTimer]);

    useEffect(() => {
        if (!disabled || !isConfirming) {
            return;
        }
        clearDeletionRequestTimer();
        queueMicrotask(() => setIsConfirming(false));
    }, [disabled, isConfirming, clearDeletionRequestTimer]);

    const handleDelete = () => {
        clearDeletionRequestTimer();
        setIsConfirming(false);
        onDelete();
    };

    const handleRequestDeletion = () => {
        clearDeletionRequestTimer();
        setIsConfirming(true);
        deletionRequestTimerRef.current = setTimeout(() => {
            deletionRequestTimerRef.current = undefined;
            setIsConfirming(false);
        }, 2000);
    };

    const renderButtonWithTooltip = () => {
        return (
            <Tooltip title={tooltip}>
                <span>{renderButton()}</span>
            </Tooltip>
        );
    };

    const renderButton = () => {
        if (label) {
            return (
                <Button
                    variant="contained"
                    startIcon={icon || <DeleteIcon />}
                    onClick={handleRequestDeletion}
                    disabled={disabled}
                    color={color || "inherit"}
                >
                    {label}
                </Button>
            );
        }
        return (
            <IconButton
                onClick={handleRequestDeletion}
                disabled={disabled}
                color={color || "inherit"}
                // Stays stable while the tooltip may explain why deletion is currently blocked.
                aria-label={ariaLabel || tooltip}
            >
                {icon || <DeleteIcon />}
            </IconButton>
        );
    };

    const showInitialButton = !isConfirming || disabled;

    return (
        <Box sx={{ ...sx }}>
            {showInitialButton ? (
                tooltip ? (
                    renderButtonWithTooltip()
                ) : (
                    renderButton()
                )
            ) : (
                <Button
                    startIcon={icon || <DeleteIcon />}
                    onClick={handleDelete}
                    disabled={disabled}
                    color={color || "inherit"}
                >
                    {t("CONFIRM_DELETION")}
                </Button>
            )}
        </Box>
    );
};

export default DeleteConfirmation;
